/**
 * Projections Summary Page
 *
 * Company-wide budget projections dashboard showing aggregated financial metrics.
 * Admin/Director/CEO page displaying 6 key metrics across all clients.
 *
 * Route: /projections
 * Access: Controlled by Sidebar visibility (admin, diretor, ceo roles)
 *
 * @module routes/_auth/projections
 */

import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useProjectionsSummary } from '@/hooks/useProjectionsSummary'
import { ProjectionCard, ProjectionCardSkeleton } from '@/components/budgets/ProjectionCard'
import Breadcrumbs from '@/components/Breadcrumbs'
import { DollarSign, TrendingDown, Receipt, TrendingUp, Users, Briefcase, AlertCircle, RefreshCw } from 'lucide-react'

/**
 * Route configuration
 * Note: Access control handled by Sidebar navigation visibility
 */
export const Route = createFileRoute('/_auth/projections')({
  component: ProjectionsPage,
})

/**
 * ProjectionsPage Component
 *
 * Displays company-wide financial projections summary with:
 * - Year selector (2020-2099)
 * - 6 metric cards showing aggregated totals
 * - Client count and last updated timestamp
 * - Loading, error, and empty states
 */
function ProjectionsPage() {
  // Year options: only 2024 and 2025
  const yearOptions = [2024, 2025]
  const currentYear = 2025
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)

  // Fetch projections data using hook
  const { data, loading, error, refetch } = useProjectionsSummary(selectedYear)

  /**
   * Handle year selection change
   */
  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(event.target.value, 10)
    setSelectedYear(year)
  }

  /**
   * Handle retry button click
   */
  const handleRetry = async () => {
    await refetch()
  }

  // Check for empty state (no budget data)
  const isEmpty = data && data.clientCount === 0

  return (
    <div className="space-y-8">
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Financeiro', href: '/projections' },
          { label: 'Projeções' }
        ]}
      />

      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 pb-2">
        <div className="flex-1">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mb-3 bg-gradient-to-r from-white via-white to-brand-primary bg-clip-text text-transparent leading-tight">
            Projeções Financeiras
          </h1>
          <p className="text-brand-gray font-body text-base max-w-2xl leading-relaxed">
            Visão consolidada das projeções orçamentárias de todos os clientes
          </p>
        </div>

        {/* Year Selector */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative">
            <label htmlFor="year-selector" className="sr-only">
              Selecionar ano
            </label>
            <select
              id="year-selector"
              value={selectedYear}
              onChange={handleYearChange}
              className="
                bg-brand-dark-light
                border border-brand-dark-lighter
                text-white
                rounded-monumenta
                px-4 py-2.5
                font-body
                text-sm
                focus:outline-none
                focus:ring-2
                focus:ring-brand-primary
                focus:border-transparent
                cursor-pointer
                min-w-[120px]
              "
              disabled={loading}
              data-testid="year-selector"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div
          className="
            bg-red-900/20
            border border-red-800/50
            rounded-monumenta
            p-6
            flex items-start gap-4
          "
          role="alert"
          data-testid="error-state"
        >
          <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-red-300 mb-2">
              Erro ao carregar projeções financeiras
            </h3>
            <p className="text-red-200/80 text-sm mb-4">
              {error.message || 'Não foi possível carregar os dados. Por favor, tente novamente.'}
            </p>
            <button
              onClick={handleRetry}
              className="
                inline-flex items-center gap-2
                bg-red-800/30
                hover:bg-red-800/50
                text-red-200
                px-4 py-2
                rounded-monumenta
                text-sm font-medium
                transition-colors
                focus:outline-none
                focus:ring-2
                focus:ring-red-500
              "
              data-testid="retry-button"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar Novamente
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && isEmpty && (
        <div
          className="
            bg-brand-dark-light
            border border-brand-dark-lighter
            rounded-monumenta
            p-12
            text-center
          "
          data-testid="empty-state"
        >
          <Briefcase className="w-16 h-16 text-brand-gray mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Nenhuma projeção encontrada para {selectedYear}
          </h3>
          <p className="text-brand-gray text-sm max-w-md mx-auto">
            Configure orçamentos de clientes para visualizar projeções consolidadas.
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div>
          {/* Metric Cards Grid - Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <ProjectionCardSkeleton />
            <ProjectionCardSkeleton />
            <ProjectionCardSkeleton />
            <ProjectionCardSkeleton />
            <ProjectionCardSkeleton />
            <ProjectionCardSkeleton />
          </div>

          {/* Summary Info - Skeleton */}
          <div className="flex flex-wrap items-center gap-6 mt-8 pt-6 border-t border-brand-dark-lighter">
            <div className="h-5 bg-brand-dark-lighter rounded w-40 animate-pulse"></div>
            <div className="h-5 bg-brand-dark-lighter rounded w-48 animate-pulse"></div>
          </div>
        </div>
      )}

      {/* Success State - Data Display */}
      {!loading && !error && data && !isEmpty && (
        <>
          {/* Metric Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <ProjectionCard
              title="Receita Total"
              value={data.projections.totalRevenue}
              icon={DollarSign}
            />
            <ProjectionCard
              title="Custos Totais"
              value={data.projections.totalCosts}
              icon={TrendingDown}
            />
            <ProjectionCard
              title="Impostos Totais"
              value={data.projections.totalTax}
              icon={Receipt}
            />
            <ProjectionCard
              title="Margem Total"
              value={data.projections.totalMargin}
              icon={TrendingUp}
            />
            <ProjectionCard
              title="Folha de Pagamento Total"
              value={data.projections.totalLabourCost}
              icon={Users}
            />
            <ProjectionCard
              title="Trabalho Aprovado Total"
              value={data.projections.totalApprovedWork}
              icon={Briefcase}
            />
          </div>

          {/* Summary Information */}
          <div
            className="
              flex flex-wrap items-center gap-6 mt-8 pt-6
              border-t border-brand-dark-lighter
            "
            data-testid="summary-info"
          >
            <div className="text-sm text-brand-gray font-body">
              <span className="font-medium text-white">{data.clientCount}</span> cliente
              {data.clientCount !== 1 ? 's' : ''} incluído{data.clientCount !== 1 ? 's' : ''}
            </div>
            <div className="text-sm text-brand-gray font-body">
              Última atualização:{' '}
              <span className="font-medium text-white">
                {new Date(data.lastUpdated).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
